window.dataLayer = window.dataLayer || [];

let currentSettings = null;

/**
 * Dynamically injects tracking scripts (Meta Pixel, GA4, GTM, Clarity, LinkedIn, Custom scripts)
 * into the browser DOM when siteSettings are loaded on the client side.
 */
export function initTrackingScripts(settings = {}) {
  if (!settings) return;
  currentSettings = settings;

  // 1. Google Tag Manager (GTM)
  if (settings.gtmId && !document.getElementById('gtm-script')) {
    const gtmScript = document.createElement('script');
    gtmScript.id = 'gtm-script';
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${settings.gtmId}');`;
    document.head.appendChild(gtmScript);
  }

  // 2. Google Analytics 4 (GA4)
  if (settings.ga4Id && !document.getElementById('ga4-script')) {
    const gaScript = document.createElement('script');
    gaScript.id = 'ga4-script';
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${settings.ga4Id}`;
    document.head.appendChild(gaScript);

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', settings.ga4Id, { send_page_view: false });
  }

  // 3. Google Ads Conversion ID
  if (settings.googleAdsId && !document.getElementById('google-ads-script')) {
    if (!settings.ga4Id) {
      const gAdsScript = document.createElement('script');
      gAdsScript.id = 'google-ads-script';
      gAdsScript.async = true;
      gAdsScript.src = `https://www.googletagmanager.com/gtag/js?id=${settings.googleAdsId}`;
      document.head.appendChild(gAdsScript);

      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
    }
    if (window.gtag) {
      window.gtag('config', settings.googleAdsId);
    }
  }

  // 4. Meta (Facebook) Pixel
  if (settings.metaPixelId) {
    if (!window.fbq && !document.getElementById('meta-pixel-script')) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          if (n.callMethod) {
            n.callMethod.apply(n, arguments);
          } else {
            n.queue.push(arguments);
          }
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.id = 'meta-pixel-script';
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    if (typeof window.fbq === 'function') {
      window.fbq('init', String(settings.metaPixelId).trim());
    }
  }

  // 5. Microsoft Clarity
  if (settings.clarityId && !document.getElementById('clarity-script')) {
    const clarityScript = document.createElement('script');
    clarityScript.id = 'clarity-script';
    clarityScript.type = 'text/javascript';
    clarityScript.innerHTML = `(function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window,document,"clarity","script","${settings.clarityId}");`;
    document.head.appendChild(clarityScript);
  }

  // 6. LinkedIn Insight Tag
  if (settings.linkedinPartnerId && !document.getElementById('linkedin-script')) {
    window._linkedin_partner_id = settings.linkedinPartnerId;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(settings.linkedinPartnerId);

    const liScript = document.createElement('script');
    liScript.id = 'linkedin-script';
    liScript.type = 'text/javascript';
    liScript.async = true;
    liScript.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    document.head.appendChild(liScript);
  }

  // 7. Additional Head Scripts (if safe script elements)
  if (settings.additionalHeadScripts && !document.getElementById('custom-head-scripts')) {
    try {
      const container = document.createElement('div');
      container.id = 'custom-head-scripts';
      container.style.display = 'none';
      container.innerHTML = settings.additionalHeadScripts;
      // Re-create scripts so the browser executes them
      const inlineScripts = container.querySelectorAll('script');
      inlineScripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        document.head.appendChild(newScript);
      });
    } catch (e) {
      console.warn('Error evaluating additionalHeadScripts:', e);
    }
  }
}

/**
 * Track SPA Route PageView across all connected platforms
 */
export function trackPageView(path = window.location.pathname, title = document.title) {
  // Push to GTM / dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });

  // Google Analytics 4
  if (typeof window.gtag === 'function' && currentSettings?.ga4Id) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    });
  }

  // Meta (Facebook) Pixel
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
}

/**
 * General custom event dispatcher
 */
export function trackEvent(event, params = {}) {
  // 1. DataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });

  // 2. Google Analytics 4 / Google Ads
  if (typeof window.gtag === 'function') {
    window.gtag('event', event, params);
  }

  // 3. Meta (Facebook) Pixel standard event mapping
  if (typeof window.fbq === 'function') {
    if (event === 'contact_form_submit' || event === 'lead') {
      window.fbq('track', 'Contact', params);
    } else if (event === 'add_to_cart') {
      window.fbq('track', 'AddToCart', params);
    } else if (event === 'initiate_checkout') {
      window.fbq('track', 'InitiateCheckout', params);
    } else if (event === 'search') {
      window.fbq('track', 'Search', params);
    } else {
      window.fbq('trackCustom', event, params);
    }
  }
}

export function initClickTracking() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-track]');
    if (el) {
      trackEvent(el.dataset.track, {
        label: el.dataset.trackLabel || el.textContent?.trim()?.slice(0, 80),
        href: el.href || el.dataset.href,
      });
    }
    const link = e.target.closest('a[href^="tel:"]');
    if (link) trackEvent('phone_click', { phone: link.href.replace('tel:', '') });
    const mail = e.target.closest('a[href^="mailto:"]');
    if (mail) trackEvent('email_click', { email: mail.href.replace('mailto:', '') });
    const wa = e.target.closest('a[href*="wa.me"], a[href*="whatsapp"]');
    if (wa) trackEvent('whatsapp_click', { href: wa.href });
  });
}

export function trackPurchase(orderData = {}) {
  const purchasePayload = {
    transaction_id: orderData.transactionId || orderData.orderId,
    value: Number(orderData.value || orderData.total || 0),
    currency: orderData.currency || 'EUR',
    items: orderData.items || [],
  };

  // 1. DataLayer
  trackEvent('purchase', purchasePayload);

  // 2. Google Analytics (purchase event)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'purchase', purchasePayload);
  }

  // 3. Meta (Facebook) Pixel Purchase
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Purchase', {
      value: purchasePayload.value,
      currency: purchasePayload.currency,
      content_type: 'product',
      contents: (purchasePayload.items || []).map((item) => ({
        id: item._id || item.id,
        quantity: item.quantity || 1,
        item_price: item.price,
      })),
    });
  }
}

export function trackLead(type, data = {}) {
  const leadPayload = { lead_type: type, ...data };
  trackEvent('lead', leadPayload);
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', leadPayload);
  }
}

